import markUrl from '../../assets/flik-mark-white.png';

/**
 * The complete FLIK brand symbol — the flicking hand with its gold sparks —
 * served from `flik-mark-white.png` and used for the activity fallback (unknown
 * types) across the app and the map pin. The whole hand, not just the sparks.
 *
 * The PNG is used as a CSS **mask** (its alpha is the full symbol shape) over a
 * solid `color`, so the symbol recolours to any tone — inheriting `currentColor`
 * by default, so it adapts to its background everywhere (dark on light surfaces,
 * light on dark). The mask is scaled/positioned to crop to the symbol's content
 * box (a per-pixel scan of every non-transparent pixel: the hand runs almost the
 * full height of the tall canvas) and fit it, centred, inside the icon box — no
 * asset is modified.
 */
export { markUrl as FLIK_MARK_URL };

// Content bounding box of the complete symbol within the 212×384 source, from a
// per-pixel scan of ALL non-transparent pixels (white hand + gold sparks). The
// hand is nearly the full canvas height, so the symbol is tall and narrow.
const IMG_W = 212;
const CONTENT_X = 8;
const CONTENT_Y = 7;
const CONTENT_W = 196;
const CONTENT_H = 369;

// Fit the whole symbol inside a `size`×`size` box (contain) and centre it. As it
// is taller than wide, height fills the box and it sits centred horizontally.
function crop(size) {
  const scale = size / Math.max(CONTENT_W, CONTENT_H);
  return {
    maskSize: `${IMG_W * scale}px auto`,
    posX: `${(size - CONTENT_W * scale) / 2 - CONTENT_X * scale}px`,
    posY: `${(size - CONTENT_H * scale) / 2 - CONTENT_Y * scale}px`,
  };
}

/**
 * React `style` for the symbol at `size`, coloured with `color` (default
 * `currentColor`, so it takes the surrounding text colour). Uses the PNG as a
 * mask cropped to the symbol content.
 */
export function flikMarkImgStyle(size, color = 'currentColor') {
  const { maskSize, posX, posY } = crop(size);
  return {
    display: 'inline-block',
    width: `${size}px`,
    height: `${size}px`,
    backgroundColor: color,
    WebkitMaskImage: `url(${markUrl})`,
    maskImage: `url(${markUrl})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskSize: maskSize,
    maskSize,
    WebkitMaskPosition: `${posX} ${posY}`,
    maskPosition: `${posX} ${posY}`,
  };
}

/** Same as `flikMarkImgStyle`, as an inline-styled `<span>` HTML string. */
export function flikMarkImgHtml(size, color = 'currentColor') {
  const { maskSize, posX, posY } = crop(size);
  const mask = `url(${markUrl}) no-repeat ${posX} ${posY} / ${maskSize}`;
  return (
    `<span style="display:inline-block;width:${size}px;height:${size}px;` +
    `background-color:${color};` +
    `-webkit-mask:${mask};mask:${mask}"></span>`
  );
}
